export const formatCurrency = (
  value: number,
  currency: string = "CAD"
): string => {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format currency using accounting convention:
 * - Negative values shown as red "(amount)" instead of "-amount"
 * - Returns { text, isNegative } so the UI can apply red styling
 */
export const formatAccountingCurrency = (
  value: number,
  currency: string = "CAD"
): { text: string; isNegative: boolean } => {
  const rounded = Math.round(value);
  const isNegative = rounded < 0;
  const absFormatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));

  return {
    text: isNegative ? `(${absFormatted})` : absFormatted,
    isNegative,
  };
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("en-CA").format(value);
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

/**
 * Format a ratio (0.07 → "7%") with up to 2 decimal places,
 * trailing zeros stripped. Used for display in projection reports.
 */
export const formatRatioAsPercentage = (ratio: number): string => {
  const pct = ratio * 100;
  const formatted = pct.toFixed(2).replace(/\.?0+$/, "");
  return `${formatted}%`;
};

/**
 * Display a value as a whole number of years (e.g., 32)
 * Use this for all year/age display values on the dashboard
 */
export const displayYears = (value: number): string => {
  return Math.round(value).toString();
};

/**
 * Display a value as a percentage with 1-digit post-decimal precision (e.g., 47.3%)
 * Input is a ratio (e.g., 0.473 → "47.3%")
 * Use this for all percentage display values on the dashboard
 */
export const displayPercentage = (ratio: number): string => {
  return `${(ratio * 100).toFixed(1)}%`;
};

/**
 * Display a value as a whole number (no decimals)
 * Use this for all non-percentage, non-currency numeric display values
 */
export const displayWholeNumber = (value: number): string => {
  return Math.round(value).toString();
};

/**
 * Display an age with up to 1 decimal place.
 * Uses toFixed(1) but strips trailing ".0" for whole-number ages.
 * Examples: 65 → "65", 65.3 → "65.3", 65.0 → "65"
 * Use this for Retirement Age and Life Expectancy display values.
 */
export const displayAge = (value: number): string => {
  return value.toFixed(1).replace(/\.0$/, "");
};

export const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

// Helper function to format Y-axis values for charts (supports negative values)
export const formatYAxisValue = (value: number, currency: string): string => {
  const currencySymbol = currency === "CAD" ? "$" : currency;
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (absValue >= 1000000) {
    return `${sign}${currencySymbol}${(absValue / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `${sign}${currencySymbol}${(absValue / 1000).toFixed(0)}K`;
  }
  return `${sign}${currencySymbol}${absValue}`;
};
